import { Link } from "react-router-dom";
import type { MappingGate } from "@/lib/types";
import { useOrgPath } from "@/lib/useOrgNav";

/**
 * Non-blocking warning shown above Overview content when a connected
 * provider has pushed native event types we don't recognize. Known
 * events are auto-mapped through the provider's built-in defaults —
 * the dashboard's derived metrics (Revenue, ROAS, Trials, CVRs)
 * always render, not gated on a human clicking Save. This banner
 * just lets the admin know a new native event is sitting un-
 * classified and invites them to review the mapping page.
 */
export function MappingGateBanner({ gate }: { gate: MappingGate }) {
  const orgPath = useOrgPath();
  const unknowns = gate.unknown_event_types ?? [];
  if (unknowns.length === 0) return null;

  const byProvider = new Map<string, string[]>();
  for (const u of unknowns) {
    const list = byProvider.get(u.provider_code) ?? [];
    list.push(u.native_event_type);
    byProvider.set(u.provider_code, list);
  }
  const summary = [...byProvider.entries()]
    .map(
      ([p, evts]) =>
        `${p}: ${evts.slice(0, 3).join(", ")}${evts.length > 3 ? ` (+${evts.length - 3} more)` : ""}`
    )
    .join("; ");

  return (
    <div
      className="card"
      style={{
        padding: "12px 16px",
        marginBottom: 16,
        background: "var(--c-warn-50)",
        borderColor: "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
      }}
    >
      <div>
        <div style={{ fontWeight: 600, fontSize: 13.5, color: "var(--c-warn-500)" }}>
          New event types need review
        </div>
        <div style={{ fontSize: 12.5, color: "var(--c-text-2)", marginTop: 2 }}>
          We received event types from your provider that aren&apos;t in
          our built-in mapping — dashboard numbers continue to work
          from the known events, but these new ones won&apos;t contribute
          until you classify them: {summary}.
        </div>
      </div>
      <Link
        to={orgPath("/settings/integrations")}
        style={{
          color: "var(--c-warn-500)",
          fontSize: 13,
          fontWeight: 500,
          whiteSpace: "nowrap",
        }}
      >
        Review mapping →
      </Link>
    </div>
  );
}
